import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, User, Share2, MessageCircle } from 'lucide-react';
import { FadeIn } from '@/components/Animations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPost(data);
        } else {
          setError('Post not found');
        }
      } catch (err) {
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-mlm"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-20" data-testid="blog-post-error">
        <div className="container-wide py-32 text-center">
          <h1 className="text-3xl font-heading font-bold text-slate-900 mb-4">
            {error || 'Post not found'}
          </h1>
          <p className="text-slate-600 mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold">
            <Link to="/insights">
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to Insights
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="blog-post-page">
      {/* Hero */}
      <section className="py-12 bg-gradient-to-br from-slate-50 via-purple-50/30 to-white">
        <div className="container-wide">
          <FadeIn>
            <Link 
              to="/insights" 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-primary-mlm mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </Link>
            
            <span className="inline-block px-3 py-1 bg-purple-100 text-primary-mlm text-xs font-semibold rounded-full mb-4">
              {post.category}
            </span>
            
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6 max-w-4xl">
              {post.title}
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <FadeIn className="lg:col-span-2">
              <article>
                {post.image_url && (
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full rounded-2xl mb-8 shadow-lg"
                  />
                )}
                
                <div 
                  className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-primary-mlm"
                  dangerouslySetInnerHTML={{ __html: post.content || `<p>${post.excerpt}</p>` }}
                />
              </article>
            </FadeIn>

            {/* Sidebar */}
            <FadeIn delay={0.2}>
              <aside className="space-y-8">
                {/* Share */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Share2 className="w-5 h-5" />
                    Share This Article
                  </h3>
                  <div className="flex gap-3">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-slate-200 hover:bg-primary-mlm hover:text-white flex items-center justify-center transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-slate-200 hover:bg-primary-mlm hover:text-white flex items-center justify-center transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-slate-200 hover:bg-primary-mlm hover:text-white flex items-center justify-center transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-slate-200 hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-colors"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </a>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-br from-primary-mlm to-purple-700 rounded-2xl p-6 text-white">
                  <h3 className="font-semibold text-white mb-3">
                    Join the Movement
                  </h3>
                  <p className="text-purple-100 text-sm mb-4">
                    Support rider safety and empowerment through corporate partnership.
                  </p>
                  <Button
                    asChild
                    className="w-full bg-white text-primary-mlm hover:bg-slate-100 rounded-full font-semibold"
                  >
                    <Link to="/corporate-csi">Learn More</Link>
                  </Button>
                </div>
              </aside>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
